/**
 * Who a hub `users` row is — the rules the settings directory renders by
 * (HEADLESS-HUB-ONBOARDING.md D1).
 *
 * Passwordless work made rows without an email normal: the commissioning
 * anchor (`username: "bootstrap"`), cloud projections, installer rows. The
 * settings list painted every one of them as a person — initials, a mail line
 * ("No email" when the column was null) and a role badge — so the machine that
 * claimed the dev hub showed up as `Windows PC`, initials `WP`, mail line
 * "Brez e-pošte", role `owner`.
 *
 * Three rules, all pure and all under test (`userDirectory.test.ts`):
 *
 *  0. A person is named by their name, else the address they are reached
 *     through — never by `username` (card `t_e56dc2b2`, D6). `username` is a
 *     DERIVED column (hub card `t_a8eb402a`): the invite path writes the email
 *     into it, a projection gets `cloud_<hash>`, the anchor `bootstrap`.
 *  1. The anchor row is the SERVER'S OWNER, not a person's name. The hub names
 *     it after whichever device claimed the hub (`HubIdentityService` takes the
 *     client's `deviceName`, and `defaultDeviceName()` sends the literal
 *     "Windows PC" for a Windows user agent), so until an account is linked the
 *     row is labelled by what it is and the device-derived string is not
 *     painted as a person (`personLabel` → null).
 *  2. A row with no email gets an ACCOUNT STATE, never an invented mail line:
 *     `linked` (a cloud account is attached), `invited` (an address is on the
 *     row) or `unlinked` (neither).
 *  3. A local password is the legacy `admin` escape hatch and nothing else. The
 *     hub refuses one on any other row (`POST /api/users` answers
 *     `password_not_allowed`), and `PUT /users/:id/password` refuses a
 *     non-`legacy` identity — a row linked to a cloud account signs in with
 *     that account — so the field is only offered where it can work.
 *  4. **A machine is a device, never a person** (card `t_7fd5e611`, plan §4.2).
 *     The hub's `users` table also holds rows for machines: its own `system`
 *     account, the **adoption anchors** a claiming machine left behind, and the
 *     machine/test rows the ops pass disabled. They own dashboards, devices and
 *     groups (their rows cannot be deleted) but they are not people, so the
 *     People list must not paint them — `isDeviceRow` below decides, and they
 *     are never listed anywhere (`scripts/cleanup-machine-rows.cjs` retires
 *     them on the hub).
 */

/** The username the hub gives its anchor user (`BOOTSTRAP_USERNAME` in otto). */
export const ANCHOR_USERNAME = 'bootstrap';

/**
 * The handle an anchor carries once a re-arm retired it
 * (`hub-adoption-state.cjs --re-arm` renames the row instead of re-using
 * `bootstrap`, which is unique and must never come back).
 */
export const RETIRED_ANCHOR_PREFIX = 'anchor-retired-';

/**
 * The slice of a `/users` row these rules read.
 *
 * `authKind` is deliberately absent: the hub's `getAllUsers()` does not select
 * `users.auth_kind`, so the app can only see the handle an anchor carries
 * (`ANCHOR_USERNAME` / `RETIRED_ANCHOR_PREFIX`) — which is what a re-arm writes.
 */
export interface DirectoryUser {
  /** The row's primary key (`users.user_id`) — read by a list's `:key`, not by the rules. */
  id?: string;
  username?: string | null;
  fullName?: string | null;
  email?: string | null;
  role?: string | null;
  cloudUserId?: string | null;
  enabled?: boolean | null;
  /**
   * The hours this person may sign in (card `t_2f0c4a91`) — ISO instants, absent
   * or `null` when the row has no bound on that side. The hub enforces them at
   * every door that mints a session; the person dialog is where an admin sets
   * them, and the invite's `installer` role is what they usually go with.
   */
  loginWindowStart?: string | null;
  loginWindowEnd?: string | null;
  createdAt?: string | null;
}

/** True for the hub's anchor row — the account the hub itself owns. */
export function isServerOwnerRow(user: DirectoryUser | null | undefined): boolean {
  return (user?.username ?? '').trim().toLowerCase() === ANCHOR_USERNAME;
}

/**
 * True for the hub's **adoption anchor** under either of its handles: the one it
 * is minted with (`bootstrap`) and the one a re-arm retires it to
 * (`anchor-retired-<stamp>`).
 *
 * The row is the hub's own account — the machine that claimed the hub acts
 * through it — and the hub names it after that machine, so it is an anchor
 * whether or not an account has since been linked to it. Whether it is a
 * **person** is a separate question (`isDeviceRow`): an anchor with a real
 * account on it is the owner's own row and must stay in the People list.
 */
export function isAdoptionAnchor(user: DirectoryUser | null | undefined): boolean {
  const handle = (user?.username ?? '').trim().toLowerCase();
  return handle === ANCHOR_USERNAME || handle.startsWith(RETIRED_ANCHOR_PREFIX);
}

/**
 * An address no person can be reached at: `…@otto.local` is what this hub's
 * test harnesses write (`hue-test`, `mobile-chrome-check`, `dash-grid-check`),
 * and `.local` (mDNS, RFC 6762) and `.invalid` (RFC 2606) are reserved and can
 * receive nothing at all. An address is how a person is reached, so one of these
 * marks the row as a machine's.
 */
export function isMachineAddress(email: string | null | undefined): boolean {
  const address = (email ?? '').trim().toLowerCase();
  const at = address.lastIndexOf('@');
  if (at <= 0) return false;
  const domain = address.slice(at + 1);
  return domain === 'localhost' || domain === 'local' || domain.endsWith('.local') || domain.endsWith('.invalid');
}

/**
 * Is a **person's** account on this row — a linked cloud account, or an address
 * a person is actually reached through? A machine fixture address is not one.
 */
export function hasPersonAccount(user: DirectoryUser | null | undefined): boolean {
  if (!user) return false;
  if (user.cloudUserId) return true;
  const email = accountEmail(user);
  return email !== null && !isMachineAddress(email);
}

/**
 * Is this row something the hub holds for a **machine**, not for a person?
 *
 * Measured on the Dev hub (2026-09-23, card `t_7fd5e611`): eleven rows came back
 * from `GET /api/users` and five of them were machine/test rows, two were
 * retired adoption anchors painting as "Windows PC" and "Owner", and one was the
 * hub's own `system` account. None of them is a person, and none of them can be
 * deleted (dashboards, `connected_devices`, `device_bindings` and groups point
 * at them), so the fix is the rendering, and this predicate is it:
 *
 *   1. `role: 'system'` — the hub's own account (`agent | Otto`);
 *   2. an adoption anchor with **no person's account on it** — the claiming
 *      machine's own row, named after that machine ("Windows PC", "Owner"). An
 *      anchor that *has* an account is the owner's row and stays a person;
 *   3. a machine's own address (`…@otto.local`) with no account linked;
 *   4. **disabled and unreachable** — the machine/test rows the ops pass
 *      disabled have no real address either, so nothing can reach them. A
 *      disabled person keeps their address and stays in People;
 *   5. nothing names a person at all: no name and no address (`personLabel` is
 *      `null`), because the derived handle is not a name.
 *
 * Everything else is a person — including a legacy local account with only a
 * name, which is why this is not "has an address".
 */
export function isDeviceRow(user: DirectoryUser | null | undefined): boolean {
  if (!user) return false;
  if (user.role === 'system') return true;
  if (isAdoptionAnchor(user) && !hasPersonAccount(user)) return true;
  if (isMachineAddress(accountEmail(user)) && !user.cloudUserId) return true;
  if (user.enabled === false && !accountEmail(user)) return true;
  return personLabel(user) === null;
}

/** Which account state line a row gets. */
export type AccountState = 'linked' | 'invited' | 'unlinked';

export function accountState(user: DirectoryUser): AccountState {
  if (user.cloudUserId) return 'linked';
  return accountEmail(user) ? 'invited' : 'unlinked';
}

/** The address a state line may name, or null when the row has none. */
export function accountEmail(user: DirectoryUser): string | null {
  const email = user.email?.trim();
  return email ? email : null;
}

/**
 * The row's own label — `null` when the row names nobody of its own (the anchor
 * row whose stored name is the claiming machine's, or a row with neither a name
 * nor an address).
 *
 * Rule 0: the name, else the address — never the handle. A row with neither is
 * `null`, which is a truthful "this row has no name", not a handle painted as
 * one (`bootstrap`, `cloud_9f3…`, `hue-test`).
 *
 * An unlinked anchor is a **machine row** (`isDeviceRow`), and the settings page
 * renders it under Devices with `retiredAnchorLabel` rather than here — but the
 * `null` stays, so no surface can fall back to painting its stored
 * device-derived name ("Windows PC", "Owner") as a person's.
 */
export function personLabel(user: DirectoryUser): string | null {
  // Either anchor handle, not just `bootstrap`: a re-arm renames the row, and a
  // renamed anchor that kept its label would paint the claiming machine's name
  // ("Windows PC", "Owner") as a person's again (card `t_7fd5e611`).
  if (isAdoptionAnchor(user) && accountState(user) !== 'linked') return null;
  return user.fullName?.trim() || accountEmail(user) || null;
}

/**
 * Can this row hold a local password? Only the legacy `admin` escape hatch: an
 * `admin` row with no cloud account attached.
 *
 * **This is an exact match on the ROW's role on purpose — do not sweep it to
 * `isAdminRole()`** (card `t_68e3b0a5` asked the question explicitly). It is not
 * the caller-role question that became a floor on F4's ladder: the hub's rule is
 * that a local password belongs to `admin` and nothing else, so
 * `POST /api/users` answers `password_not_allowed` for every other role — an
 * `owner` row is refused one (`owner` signs in with the account that owns the
 * hub, never with a hub-local secret), as is an already-linked `admin` row.
 * Offering the field where the hub would refuse it is the defect this predicate
 * exists to prevent. `roles.test.ts` pins this file (and `UserModal.vue`) as the
 * two legitimate survivors of its caller-role tripwire.
 */
export function canHoldLocalPassword(user: DirectoryUser | null | undefined): boolean {
  if (!user) return false;
  // Exact, not a floor: see above — the hub refuses a password on any other role.
  return user.role === 'admin' && !user.cloudUserId;
}
